import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import type { ITextMessage } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type SendTextVariables = {
	conversationId: string;
	text: string;
};

/**
 * Sends a plain-text message. The endpoint is multipart because it also
 * accepts attachments (`TextMessagingViewModel.sendMessage`); the JSON goes in
 * the `value` field.
 */
export function useSendText(): UseMutationResult<ITextMessage, Error, SendTextVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<ITextMessage, Error, SendTextVariables>({
		mutationFn: async ({ conversationId, text }) => {
			const formData = new FormData();
			formData.append('value', JSON.stringify({ text }));
			const result = await userSession.webServiceHelper.callWebServiceAsync<ITextMessage>(
				ImpersonationBroker.composeApiUrl({ urlPath: `conversation/${conversationId}/send` }),
				'POST',
				formData
			);
			if (!result.success || !result.value) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (_message, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.textMessages(variables.conversationId) });
		},
		// The dialog toasts from its mutateAsync catch; this keeps a .mutate() caller from dropping the failure silently.
		onError: error => {
			console.error('Send text error:', error);
		},
	});
}
